import {useCallback, useEffect, useRef, useState} from 'react';
import {
  useRunPDFInconsistenciesMutation,
  useLazyGetJobStatusQuery,
} from '../../../data/pdfReviewApi';
import _ from 'lodash';
import {sleep} from '../../../util';

export const STATUS = {
  IDLE: 'idle',
  RUNNING: 'running',
  COMPLETE: 'complete',
  ERROR: 'error',
};

const DEFAULT_WAIT_MS = 5000;
const DEFAULT_RETRY_MAX = 180;

const CACHE_PREFIX = 'pdf-inconsistencies-cache';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const getCacheKey = (documentId) => `${CACHE_PREFIX}-${documentId}`;

const getCachedInconsistencies = (documentId) => {
  try {
    const raw = localStorage.getItem(getCacheKey(documentId));
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(getCacheKey(documentId));
      return null;
    }
    return cached.data;
  } catch {
    return null;
  }
};

const setCachedInconsistencies = (documentId, data) => {
  try {
    localStorage.setItem(
      getCacheKey(documentId),
      JSON.stringify({data, timestamp: Date.now()})
    );
  } catch {}
};

const clearCachedInconsistencies = (documentId) => {
  try {
    localStorage.removeItem(getCacheKey(documentId));
  } catch {}
};

const cleanupExpiredCache = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(CACHE_PREFIX))
      .forEach((key) => {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
            localStorage.removeItem(key);
          }
        } catch {
          localStorage.removeItem(key);
        }
      });
  } catch {}
};

const parseInconsistencyResults = (data) => {
  const inconsistencies = _.get(data, 'inconsistencies', []);

  if (!inconsistencies || inconsistencies.length === 0) {
    return {items: {}, openItems: {}, numItems: 0, numOpenItems: 0};
  }

  const items = {};
  const openItems = {};

  inconsistencies.forEach((inconsistency) => {
    if (inconsistency.dismissed) return;

    const item = {
      ...inconsistency,
      type: 'pdf-inconsistencies',
    };

    items[inconsistency.id] = item;

    if (!inconsistency.dismissed) {
      openItems[inconsistency.id] = item;
    }
  });

  return {
    items,
    openItems,
    numItems: Object.keys(items).length,
    numOpenItems: Object.keys(openItems).length,
  };
};

export const usePdfInconsistencyCheck = ({documentId, isPdfParsed = false}) => {
  const [status, setStatus] = useState(() =>
    getCachedInconsistencies(documentId) ? STATUS.COMPLETE : STATUS.IDLE
  );
  const [inconsistenciesData, setInconsistenciesData] = useState(
    () => getCachedInconsistencies(documentId) || null
  );
  const [error, setError] = useState(null);

  const isMountedRef = useRef(true);
  const hasStartedRef = useRef(false);

  const inconsistenciesDataRef = useRef(inconsistenciesData);
  useEffect(() => {
    inconsistenciesDataRef.current = inconsistenciesData;
  }, [inconsistenciesData]);

  const [runPDFInconsistencies] = useRunPDFInconsistenciesMutation();
  const [getJobStatus] = useLazyGetJobStatusQuery();

  const startInconsistenciesCheck = useCallback(async () => {
    if (!documentId) return;

    setStatus(STATUS.RUNNING);
    setError(null);

    try {
      const result = await runPDFInconsistencies({documentId}).unwrap();

      if (!isMountedRef.current) return;

      const jobId = result?.job_id;
      if (jobId == null) {
        setError('No job ID from inconsistencies check.');
        setStatus(STATUS.ERROR);
        return;
      }

      await checkJobStatus(jobId);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('[usePdfInconsistencyCheck] Error:', err);
      setError(err?.data?.message || 'Failed to run inconsistencies check.');
      setStatus(STATUS.ERROR);
    }
  }, [documentId, runPDFInconsistencies]);

  const checkJobStatus = async (jobId) => {
    if (_.isNil(jobId)) {
      console.error('No job ID found', {jobId});
      return;
    }

    const pollJobStatus = async (tries = 0) => {
      if (!window.location.href.includes(documentId)) {
        console.log('Job has been cancelled', {jobId, documentId, tries});
        return;
      }
      try {
        const _jobResult = await getJobStatus(
          {jobId, checkType: 'pdf-inconsistencies'},
          false
        ).unwrap();

        if (_jobResult.status === 'completed') {
          const _data = _jobResult.results;
          const parsed = parseInconsistencyResults(_data);
          setInconsistenciesData(parsed);
          setCachedInconsistencies(documentId, parsed);
          setStatus(STATUS.COMPLETE);
        } else if (_.includes(['started', 'processing'], _jobResult.status)) {
          console.log('Job is processing', {
            jobId,
            checkType: 'pdf-inconsistencies',
            tries,
          });
          if (tries < DEFAULT_RETRY_MAX) {
            await sleep(DEFAULT_WAIT_MS);
            return pollJobStatus(tries + 1);
          } else {
            console.error('Too many retries, aborting.', {
              jobId,
              checkType: 'pdf-inconsistencies',
            });
            setStatus(STATUS.ERROR);
          }
        } else if (_jobResult.status === 'failed') {
          console.error('Inconsistencies job failed', {
            jobId,
            checkType: 'pdf-inconsistencies',
          });
          setError(_jobResult.user_message || null);
          setStatus(STATUS.ERROR);
        }
      } catch (_error) {
        console.error('Error processing job', {error: _error});
        setStatus(STATUS.ERROR);
      }
    };

    await pollJobStatus(0);
  };

  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const startCheck = useCallback(async () => {
    if (
      inconsistenciesDataRef.current ||
      statusRef.current === STATUS.RUNNING
    ) {
      return;
    }
    if (!isPdfParsed) {
      return;
    }
    await startInconsistenciesCheck();
  }, [startInconsistenciesCheck, isPdfParsed]);

  const rerunInconsistenciesCheck = useCallback(() => {
    clearCachedInconsistencies(documentId);
    inconsistenciesDataRef.current = null;
    setInconsistenciesData(null);
    startInconsistenciesCheck();
  }, [documentId, startInconsistenciesCheck]);

  const dismissInconsistency = useCallback(
    (inconsistencyId) => {
      setInconsistenciesData((prev) => {
        if (!prev) return prev;
        const newItems = {...prev.items};
        const newOpenItems = {...prev.openItems};
        delete newItems[inconsistencyId];
        delete newOpenItems[inconsistencyId];
        const updated = {
          items: newItems,
          openItems: newOpenItems,
          numItems: Object.keys(newItems).length,
          numOpenItems: Object.keys(newOpenItems).length,
        };
        setCachedInconsistencies(documentId, updated);
        return updated;
      });
    },
    [documentId]
  );

  const reset = useCallback(() => {
    clearCachedInconsistencies(documentId);
    setStatus(STATUS.IDLE);
    setInconsistenciesData(null);
    setError(null);
    hasStartedRef.current = false;
    inconsistenciesDataRef.current = null;
  }, [documentId]);

  useEffect(() => {
    cleanupExpiredCache();
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    status,
    inconsistenciesData,
    error,
    isLoading: status === STATUS.RUNNING,
    isRunning: status === STATUS.RUNNING,
    isComplete: status === STATUS.COMPLETE,
    isError: status === STATUS.ERROR,
    hasIssues: (inconsistenciesData?.numOpenItems ?? 0) > 0,
    startCheck,
    rerunInconsistenciesCheck,
    dismissInconsistency,
    reset,
  };
};

export default usePdfInconsistencyCheck;
